// Top-20 Indian steel / scrap hubs used by the hyper-local /locations/:citySlug
// pages and the programmatic /rates/:materialSlug/:citySlug pages.
//
// `benchmarkCluster` points at one of the eight priced clusters in
// `scrap_indices`; `diffPct` is the indicative ex-yard differential of this city
// against that benchmark, used to render a localised rate.

export interface LocationMeta {
  slug: string;
  name: string;
  state: string;
  region: "East" | "West" | "North" | "South" | "Central";
  lat: number;
  lng: number;
  /** cluster_slug in scrap_indices used as the pricing benchmark */
  benchmarkCluster: string;
  /** indicative % differential vs the benchmark cluster */
  diffPct: number;
  blurb: string;
  industries: string[];
  stats: {
    activeYards: number;
    monthlyTonnage: number;
    avgSettlementHours: number;
    verifiedBuyers: number;
  };
  testimonial: {
    quote: string;
    author: string;
    role: string;
  };
  /** Nearby hubs (slugs) for internal linking */
  nearby: string[];
}

export const LOCATIONS: LocationMeta[] = [
  {
    slug: "siliguri",
    name: "Siliguri",
    state: "West Bengal",
    region: "East",
    lat: 26.7271,
    lng: 88.3953,
    benchmarkCluster: "durgapur",
    diffPct: -2.5,
    blurb:
      "Siliguri is the collection gateway for scrap moving out of North Bengal, Sikkim and the North-East. Yards here aggregate demolition and railway scrap that feeds furnaces in Durgapur, Asansol and Bihar.",
    industries: ["Scrap aggregation yards", "Railway and demolition scrap", "Re-rolling mills"],
    stats: { activeYards: 38, monthlyTonnage: 9400, avgSettlementHours: 26, verifiedBuyers: 21 },
    testimonial: {
      quote:
        "Earlier my Siliguri lots moved through two brokers before reaching a Durgapur furnace. On LohaLink the buyer locks escrow the same day and I get paid the day after weighment.",
      author: "Subir Roy",
      role: "Yard owner, Siliguri",
    },
    nearby: ["malda", "durgapur", "asansol"],
  },
  {
    slug: "durgapur",
    name: "Durgapur",
    state: "West Bengal",
    region: "East",
    lat: 23.5204,
    lng: 87.3119,
    benchmarkCluster: "durgapur",
    diffPct: 0,
    blurb:
      "Durgapur's induction-furnace belt is the largest single consumer of HMS 1 and HMS 2 in eastern India, with sponge iron and billet demand tracking SAIL realisations closely.",
    industries: ["Induction furnaces", "Long products", "Sponge iron plants"],
    stats: { activeYards: 64, monthlyTonnage: 28600, avgSettlementHours: 20, verifiedBuyers: 47 },
    testimonial: {
      quote:
        "We buy 4,000 tonnes a month. Weighbridge-verified dispatch photos and escrow release mean we stopped writing off short-weight losses entirely.",
      author: "Pranab Chatterjee",
      role: "Purchase head, Durgapur induction plant",
    },
    nearby: ["asansol", "jamshedpur", "kolkata"],
  },
  {
    slug: "malda",
    name: "Malda",
    state: "West Bengal",
    region: "East",
    lat: 25.0119,
    lng: 88.1433,
    benchmarkCluster: "durgapur",
    diffPct: -3,
    blurb:
      "Malda supplies mixed ferrous scrap generated across Murshidabad and the Bihar border districts, moving mainly to Asansol and Durgapur furnaces by road.",
    industries: ["Scrap collection", "Agricultural machinery scrap", "Transport aggregation"],
    stats: { activeYards: 22, monthlyTonnage: 5200, avgSettlementHours: 28, verifiedBuyers: 14 },
    testimonial: {
      quote:
        "Malda yards never had direct foundry access. Listing tonnage with AI grading gave me buyers in Durgapur without a single site visit.",
      author: "Md. Rafiqul Islam",
      role: "Scrap trader, Malda",
    },
    nearby: ["siliguri", "durgapur", "kolkata"],
  },
  {
    slug: "asansol",
    name: "Asansol",
    state: "West Bengal",
    region: "East",
    lat: 23.6739,
    lng: 86.9524,
    benchmarkCluster: "durgapur",
    diffPct: -0.8,
    blurb:
      "Asansol combines colliery-belt scrap generation with a dense cluster of re-rollers and ingot makers, making it one of eastern India's most liquid ex-yard markets.",
    industries: ["Re-rolling mills", "Ingot makers", "Colliery scrap"],
    stats: { activeYards: 41, monthlyTonnage: 15800, avgSettlementHours: 22, verifiedBuyers: 33 },
    testimonial: {
      quote:
        "Rates from LohaLink match what our Asansol re-rollers actually pay, so negotiation now starts from a number both sides trust.",
      author: "Vikas Agarwal",
      role: "Re-rolling mill owner, Asansol",
    },
    nearby: ["durgapur", "jamshedpur", "malda"],
  },
  {
    slug: "jamshedpur",
    name: "Jamshedpur",
    state: "Jharkhand",
    region: "East",
    lat: 22.8046,
    lng: 86.2029,
    benchmarkCluster: "jamshedpur",
    diffPct: 0,
    blurb:
      "India's oldest integrated steel ecosystem. Jamshedpur's auto-grade and heavy-engineering scrap generation sets the benchmark for eastern ferrous pricing.",
    industries: ["Integrated steel mills", "Auto components", "Heavy engineering"],
    stats: { activeYards: 57, monthlyTonnage: 24200, avgSettlementHours: 18, verifiedBuyers: 52 },
    testimonial: {
      quote:
        "Our vendor onboarding used to take three weeks of GST and bank checks. LohaLink hands us a verified counterparty on day one.",
      author: "Ramesh Mahato",
      role: "Sourcing manager, Jamshedpur foundry",
    },
    nearby: ["durgapur", "asansol", "rourkela"],
  },
  {
    slug: "kolkata",
    name: "Kolkata",
    state: "West Bengal",
    region: "East",
    lat: 22.5726,
    lng: 88.3639,
    benchmarkCluster: "durgapur",
    diffPct: 1.2,
    blurb:
      "Kolkata's Howrah–Liluah belt is the trading capital of eastern scrap, with port-linked imported HMS and a dense network of foundries and job-work units.",
    industries: ["Foundries", "Import-fed yards", "Engineering job work"],
    stats: { activeYards: 73, monthlyTonnage: 19700, avgSettlementHours: 24, verifiedBuyers: 61 },
    testimonial: {
      quote:
        "For Howrah foundries, working capital is everything. Escrow release within 24 hours of delivery changed our purchase cycle.",
      author: "Arindam Sen",
      role: "Foundry owner, Howrah",
    },
    nearby: ["durgapur", "asansol", "malda"],
  },
  {
    slug: "raipur",
    name: "Raipur",
    state: "Chhattisgarh",
    region: "Central",
    lat: 21.2514,
    lng: 81.6296,
    benchmarkCluster: "raipur",
    diffPct: 0,
    blurb:
      "The Raipur–Bhilai corridor is India's largest sponge iron hub. Sponge, billet and scrap rates here drive secondary steel pricing across central India.",
    industries: ["Sponge iron", "Rolling mills", "Ferro alloys"],
    stats: { activeYards: 68, monthlyTonnage: 31500, avgSettlementHours: 19, verifiedBuyers: 58 },
    testimonial: {
      quote:
        "We move sponge iron and billets weekly. Having one platform with verified tonnage and tracked trucks removed our biggest operational headache.",
      author: "Suresh Agrawal",
      role: "Director, Raipur steel unit",
    },
    nearby: ["bhilai", "rourkela", "nagpur"],
  },
  {
    slug: "bhilai",
    name: "Bhilai",
    state: "Chhattisgarh",
    region: "Central",
    lat: 21.1938,
    lng: 81.3509,
    benchmarkCluster: "raipur",
    diffPct: -0.5,
    blurb:
      "Bhilai's plant-linked scrap generation and downstream rolling mills make it a natural supply-demand pair with Raipur's sponge iron kilns.",
    industries: ["Integrated plant scrap", "Rolling mills", "Fabrication units"],
    stats: { activeYards: 34, monthlyTonnage: 13400, avgSettlementHours: 21, verifiedBuyers: 29 },
    testimonial: {
      quote:
        "Bhilai buyers want certified weight, not promises. The weighbridge slip and delivery photos settle every argument before it starts.",
      author: "Deepak Sahu",
      role: "Yard owner, Bhilai",
    },
    nearby: ["raipur", "rourkela", "nagpur"],
  },
  {
    slug: "rourkela",
    name: "Rourkela",
    state: "Odisha",
    region: "East",
    lat: 22.2604,
    lng: 84.8536,
    benchmarkCluster: "raipur",
    diffPct: -1.5,
    blurb:
      "Rourkela pairs integrated plant scrap with Odisha's iron ore and pellet supply, feeding sponge iron and ferro-alloy units across the mineral belt.",
    industries: ["Sponge iron", "Ferro alloys", "Pellet plants"],
    stats: { activeYards: 29, monthlyTonnage: 11200, avgSettlementHours: 23, verifiedBuyers: 26 },
    testimonial: {
      quote:
        "Pellet and sponge lots used to be booked on phone calls. Now the price, tonnage and payment terms are all documented before the truck loads.",
      author: "Bibhu Patnaik",
      role: "Trader, Rourkela",
    },
    nearby: ["raipur", "bhilai", "jamshedpur"],
  },
  {
    slug: "nagpur",
    name: "Nagpur",
    state: "Maharashtra",
    region: "Central",
    lat: 21.1458,
    lng: 79.0882,
    benchmarkCluster: "raipur",
    diffPct: 1.5,
    blurb:
      "Nagpur sits at the road junction between central India's sponge iron kilns and western India's foundries, making it a key transit and re-rolling market.",
    industries: ["Re-rolling mills", "Foundries", "Transport hubs"],
    stats: { activeYards: 31, monthlyTonnage: 9800, avgSettlementHours: 24, verifiedBuyers: 27 },
    testimonial: {
      quote:
        "Live truck tracking matters on the Raipur–Nagpur run. Our furnace team plans the charge before the vehicle reaches the gate.",
      author: "Nitin Deshmukh",
      role: "Plant manager, Nagpur",
    },
    nearby: ["raipur", "bhilai", "pune"],
  },
  {
    slug: "mandi-gobindgarh",
    name: "Mandi Gobindgarh",
    state: "Punjab",
    region: "North",
    lat: 30.6667,
    lng: 76.3,
    benchmarkCluster: "mandi-gobindgarh",
    diffPct: 0,
    blurb:
      "The 'Steel City of the North'. Mandi Gobindgarh's re-rollers and induction furnaces post India's most-watched ex-yard HMS quotations.",
    industries: ["Re-rolling mills", "TMT bars", "Wire rod"],
    stats: { activeYards: 82, monthlyTonnage: 34800, avgSettlementHours: 17, verifiedBuyers: 74 },
    testimonial: {
      quote:
        "In Mandi the rate changes twice a day. A benchmarked live price stops both sides wasting an hour arguing over where the market is.",
      author: "Harpreet Singh",
      role: "Furnace owner, Mandi Gobindgarh",
    },
    nearby: ["ludhiana", "ghaziabad", "kanpur"],
  },
  {
    slug: "ludhiana",
    name: "Ludhiana",
    state: "Punjab",
    region: "North",
    lat: 30.901,
    lng: 75.8573,
    benchmarkCluster: "mandi-gobindgarh",
    diffPct: 0.8,
    blurb:
      "Ludhiana's cycle-parts, fastener and auto-component clusters generate high-quality machining and sheet scrap consumed locally and in Mandi Gobindgarh.",
    industries: ["Auto components", "Fasteners", "Machining units"],
    stats: { activeYards: 59, monthlyTonnage: 17300, avgSettlementHours: 20, verifiedBuyers: 48 },
    testimonial: {
      quote:
        "Our turnings used to sell at whatever the local kabadi offered. Competitive bids from verified buyers lifted realisation noticeably.",
      author: "Gurdeep Sethi",
      role: "MSME unit owner, Ludhiana",
    },
    nearby: ["mandi-gobindgarh", "ghaziabad", "kanpur"],
  },
  {
    slug: "ghaziabad",
    name: "Ghaziabad",
    state: "Uttar Pradesh",
    region: "North",
    lat: 28.6692,
    lng: 77.4538,
    benchmarkCluster: "ghaziabad",
    diffPct: 0,
    blurb:
      "The Delhi-NCR scrap belt spanning Ghaziabad, Loni and Muzaffarnagar feeds North India's rebar and construction demand with consistently liquid HMS pricing.",
    industries: ["Re-rolling mills", "Rebar", "Engineering scrap"],
    stats: { activeYards: 77, monthlyTonnage: 26900, avgSettlementHours: 19, verifiedBuyers: 66 },
    testimonial: {
      quote:
        "NCR is full of intermediaries. Direct escrow-backed deals with yards cut roughly ₹700 a tonne out of our landed cost.",
      author: "Ankit Jain",
      role: "Procurement lead, Ghaziabad rolling mill",
    },
    nearby: ["kanpur", "mandi-gobindgarh", "ludhiana"],
  },
  {
    slug: "kanpur",
    name: "Kanpur",
    state: "Uttar Pradesh",
    region: "North",
    lat: 26.4499,
    lng: 80.3319,
    benchmarkCluster: "ghaziabad",
    diffPct: -1.2,
    blurb:
      "Kanpur's engineering, textile-machinery and defence-supplier base generates steady ferrous scrap absorbed by UP's induction furnaces and re-rollers.",
    industries: ["Engineering units", "Re-rollers", "Foundries"],
    stats: { activeYards: 44, monthlyTonnage: 12600, avgSettlementHours: 23, verifiedBuyers: 37 },
    testimonial: {
      quote:
        "Payment risk was the reason we never sold outside Kanpur. Escrow removed it, so we now dispatch to Ghaziabad buyers regularly.",
      author: "Rakesh Gupta",
      role: "Yard owner, Kanpur",
    },
    nearby: ["ghaziabad", "mandi-gobindgarh", "kolkata"],
  },
  {
    slug: "mumbai",
    name: "Mumbai",
    state: "Maharashtra",
    region: "West",
    lat: 19.076,
    lng: 72.8777,
    benchmarkCluster: "mumbai",
    diffPct: 0,
    blurb:
      "Mumbai–Taloja pricing reflects imported scrap landed at JNPT plus domestic supply moving to western India's auto and infrastructure customers.",
    industries: ["Import-fed yards", "Auto components", "Construction"],
    stats: { activeYards: 88, monthlyTonnage: 29400, avgSettlementHours: 18, verifiedBuyers: 79 },
    testimonial: {
      quote:
        "Landed imported HMS and domestic lots on one screen lets us arbitrage properly instead of guessing.",
      author: "Farhan Qureshi",
      role: "Trader, Taloja",
    },
    nearby: ["pune", "rajkot", "ahmedabad"],
  },
  {
    slug: "pune",
    name: "Pune",
    state: "Maharashtra",
    region: "West",
    lat: 18.5204,
    lng: 73.8567,
    benchmarkCluster: "mumbai",
    diffPct: -1,
    blurb:
      "Pune's Chakan–Ranjangaon auto corridor produces premium press-shop and machining scrap, and its foundries are steady buyers of cast iron and shredded grades.",
    industries: ["Auto OEM press shops", "Foundries", "Machining clusters"],
    stats: { activeYards: 52, monthlyTonnage: 18900, avgSettlementHours: 19, verifiedBuyers: 55 },
    testimonial: {
      quote:
        "Auto-grade scrap needs traceability for our audits. Digital invoices and weighment records satisfy compliance without extra paperwork.",
      author: "Sandeep Kulkarni",
      role: "Materials head, Pune foundry",
    },
    nearby: ["mumbai", "nagpur", "rajkot"],
  },
  {
    slug: "rajkot",
    name: "Rajkot",
    state: "Gujarat",
    region: "West",
    lat: 22.3039,
    lng: 70.8022,
    benchmarkCluster: "ahmedabad",
    diffPct: -0.8,
    blurb:
      "Rajkot's casting and engineering cluster is one of India's densest concentrations of grey-iron foundries, consuming cast iron scrap, pig iron and CI borings daily.",
    industries: ["Grey iron foundries", "Diesel engine makers", "CNC machining"],
    stats: { activeYards: 47, monthlyTonnage: 14700, avgSettlementHours: 20, verifiedBuyers: 51 },
    testimonial: {
      quote:
        "Rajkot foundries live on cast iron scrap quality. AI grading plus photo evidence means fewer rejected trucks at the gate.",
      author: "Jignesh Patel",
      role: "Foundry owner, Rajkot",
    },
    nearby: ["ahmedabad", "mumbai", "pune"],
  },
  {
    slug: "ahmedabad",
    name: "Ahmedabad",
    state: "Gujarat",
    region: "West",
    lat: 23.0225,
    lng: 72.5714,
    benchmarkCluster: "ahmedabad",
    diffPct: 0,
    blurb:
      "Ahmedabad scrap rates are anchored to ship-breaking output from Alang–Sosiya and feed Gujarat's induction furnace and re-rolling clusters.",
    industries: ["Ship-breaking feed", "Induction furnaces", "Re-rollers"],
    stats: { activeYards: 61, monthlyTonnage: 23100, avgSettlementHours: 18, verifiedBuyers: 57 },
    testimonial: {
      quote:
        "Alang material varies lot to lot. Verified tonnage and escrow give buyers the confidence to book before inspection.",
      author: "Bhavesh Shah",
      role: "Ship-breaking scrap supplier",
    },
    nearby: ["rajkot", "mumbai", "pune"],
  },
  {
    slug: "coimbatore",
    name: "Coimbatore",
    state: "Tamil Nadu",
    region: "South",
    lat: 11.0168,
    lng: 76.9558,
    benchmarkCluster: "chennai",
    diffPct: -1,
    blurb:
      "Coimbatore's pump, motor and textile-machinery foundries are among South India's largest consumers of cast iron scrap and foundry-grade pig iron.",
    industries: ["Pump and motor foundries", "Textile machinery", "Job-work casting"],
    stats: { activeYards: 43, monthlyTonnage: 12800, avgSettlementHours: 21, verifiedBuyers: 46 },
    testimonial: {
      quote:
        "We melt cast iron every day. Consistent chemistry from verified suppliers protects our casting rejection rate.",
      author: "R. Manickam",
      role: "Foundry manager, Coimbatore",
    },
    nearby: ["chennai", "hyderabad", "mumbai"],
  },
  {
    slug: "chennai",
    name: "Chennai",
    state: "Tamil Nadu",
    region: "South",
    lat: 13.0827,
    lng: 80.2707,
    benchmarkCluster: "chennai",
    diffPct: 0,
    blurb:
      "South India's largest scrap consumption cluster. Chennai pricing factors port-side imported HMS and supply into Sriperumbudur's auto OEM ecosystem.",
    industries: ["Auto OEM supply", "Induction furnaces", "Stainless steel"],
    stats: { activeYards: 69, monthlyTonnage: 25600, avgSettlementHours: 18, verifiedBuyers: 63 },
    testimonial: {
      quote:
        "OEM-linked buyers demand documentation. LohaLink's GST-verified counterparties clear our vendor audit on the first pass.",
      author: "Karthik Raman",
      role: "Purchase manager, Sriperumbudur",
    },
    nearby: ["coimbatore", "hyderabad", "mumbai"],
  },
  {
    slug: "hyderabad",
    name: "Hyderabad",
    state: "Telangana",
    region: "South",
    lat: 17.385,
    lng: 78.4867,
    benchmarkCluster: "chennai",
    diffPct: -0.5,
    blurb:
      "Hyderabad's induction furnaces, TMT producers and fabrication units draw scrap from Telangana, Andhra Pradesh and the Bellary–Hospet belt.",
    industries: ["TMT bar producers", "Induction furnaces", "Fabrication"],
    stats: { activeYards: 55, monthlyTonnage: 20400, avgSettlementHours: 19, verifiedBuyers: 49 },
    testimonial: {
      quote:
        "Sourcing billets and sponge for our TMT line used to mean five phone calls a day. Now it is one screen and one escrow.",
      author: "Srinivas Reddy",
      role: "Director, Hyderabad TMT plant",
    },
    nearby: ["chennai", "coimbatore", "raipur"],
  },
];

export const getLocationBySlug = (slug: string) =>
  LOCATIONS.find((l) => l.slug === slug.toLowerCase());

export const getNearbyLocations = (slug: string, count = 3) => {
  const current = getLocationBySlug(slug);
  if (!current) return LOCATIONS.slice(0, count);
  const listed = current.nearby
    .map((s) => getLocationBySlug(s))
    .filter((l): l is LocationMeta => !!l);
  const filler = LOCATIONS.filter(
    (l) => l.slug !== slug && !current.nearby.includes(l.slug) && l.region === current.region,
  );
  return [...listed, ...filler].slice(0, count);
};

/** Applies the city differential to a benchmark-cluster price (INR per MT). */
export const localisePrice = (benchmarkPrice: number, diffPct: number) =>
  Math.round((benchmarkPrice * (1 + diffPct / 100)) / 10) * 10;
